import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getKYCDocSettings,
  saveKycDocSettingMode,
  kycDocumentCategory,
  addKycDocument,
  getKycDocumentById,
  updateKycDocument,
  toggleKycDocument,
  kycDocumentTypes,
  removeKycDocument,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function KycDocSettings() {
  const [settingsData, setSettingsData] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [modeForm, setModeForm] = useState({
    tenantKycMode: "manual",
    coTenantKycMode: "not_required",
  });
  const [saveLoading, setSaveLoading] = useState(false);

  // Add Modal
  const [isModalOpenAdd, setIsModalOpenAdd] = useState(false);
  const [addForm, setAddForm] = useState({
    docName: "",
    categoryId: "",
    description: "",
    isOptional: false,
    sortOrder: 0,
    docTypeCode: "",
  });
  const [addLoading, setAddLoading] = useState(false);
  const [addErrors, setAddErrors] = useState({});
  const [addKycMode, setAddKycMode] = useState("manual");

  // Edit Modal
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    docName: "",
    categoryId: "",
    description: "",
    isOptional: false,
    sortOrder: 0,
    docTypeCode: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [selectedDocId, setSelectedDocId] = useState(null);

  // Categories
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Documents
  const [docTypes, setDocTypes] = useState([]);
  const [docTypesLoading, setDocTypesLoading] = useState(false);

  const [isModalOpenDelete, setIsModalOpenDelete] = useState(false);
  const [deleteDocId, setDeleteDocId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setPageLoading(true);
    try {
      const res = await getKYCDocSettings();
      if (res.statusCode === 1) {
        setSettingsData(res.data);
        setModeForm({
          tenantKycMode: res.data.tenantKycMode || "manual",
          coTenantKycMode: res.data.coTenantKycMode || "not_required",
        });
      }
    } catch (err) {
      console.log("Failed to fetch KYC settings");
    } finally {
      setPageLoading(false);
    }
  };

  const fetchCategories = async (mode) => {
    setCategoriesLoading(true);
    try {
      const res = await kycDocumentCategory(mode);
      if (res.statusCode === 1) setCategories(res.data);
    } catch (err) {
      console.log("Failed to fetch categories");
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchDocTypes = async () => {
    setDocTypesLoading(true);
    try {
      const res = await kycDocumentTypes();
      if (res.statusCode === 1) setDocTypes(res.data);
    } catch (err) {
      console.log("Failed to fetch doc types");
    } finally {
      setDocTypesLoading(false);
    }
  };

  const handleDeleteDoc = async () => {
    setDeleteLoading(true);
    try {
      const res = await removeKycDocument(deleteDocId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenDelete(false);
        setDeleteDocId(null);
        fetchSettings();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleModeChange = (e) => {
    const { name, value } = e.target;
    setModeForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveMode = async () => {
    setSaveLoading(true);
    try {
      const res = await saveKycDocSettingMode(modeForm);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchSettings();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleToggle = async (doc) => {
    try {
      const res = await toggleKycDocument(doc.docId);
      if (res.statusCode === 1) {
        toast.success(res.message);
        fetchSettings();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    }
  };

  // ── Add ──
  const openAddModal = (kycMode) => {
    setAddKycMode(kycMode);
    setAddForm({
      docName: "",
      categoryId: "",
      description: "",
      isOptional: false,
      sortOrder: 0,
      docTypeCode: "",
    });
    setAddErrors({});
    fetchCategories(kycMode);
    fetchDocTypes();
    setIsModalOpenAdd(true);
  };

  const handleAddChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setAddErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleAddSubmit = async () => {
    const e = {};
    if (!addForm.docName) e.docName = "Document name is required.";
    if (!addForm.categoryId) e.categoryId = "Category is required.";
    if (Object.keys(e).length) {
      setAddErrors(e);
      return;
    }
    setAddLoading(true);
    try {
      const payload = {
        kycMode: addKycMode,
        categoryId: Number(addForm.categoryId),
        docName: addForm.docName,
        docTypeCode: addForm.docTypeCode,
        description: addForm.description,
        isOptional: addForm.isOptional,
        sortOrder: Number(addForm.sortOrder),
      };
      const res = await addKycDocument(payload);
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenAdd(false);
        fetchSettings();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setAddLoading(false);
    }
  };

  // ── Edit ──
  const openEditModal = async (docId, kycMode) => {
    setSelectedDocId(docId);
    try {
      const res = await getKycDocumentById(docId);
      if (res.statusCode === 1) {
        const d = res.data;
        setEditForm({
          docName: d.docName || "",
          docTypeCode: d.docTypeCode || "",
          categoryId: d.categoryId || "",
          description: d.description || "",
          isOptional: d.isOptional,
          sortOrder: d.sortOrder || 0,
        });
        setEditErrors({});
        fetchCategories(kycMode);
        fetchDocTypes();
        setIsModalOpenEdit(true);
      }
    } catch (err) {
      toast.error("Failed to load document.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleEditSubmit = async () => {
    const e = {};
    if (!editForm.docName) e.docName = "Document name is required.";
    if (!editForm.categoryId) e.categoryId = "Category is required.";
    if (Object.keys(e).length) {
      setEditErrors(e);
      return;
    }
    setEditLoading(true);
    try {
      const payload = {
        categoryId: Number(editForm.categoryId),
        docName: editForm.docName,
        docTypeCode: editForm.docTypeCode,
        description: editForm.description,
        isOptional: editForm.isOptional,
        sortOrder: Number(editForm.sortOrder),
      };
      const res = await updateKycDocument(selectedDocId, payload);
      if (res.statusCode === 1) {
        toast.success(res.message );
        setIsModalOpenEdit(false);
        fetchSettings();
      } else toast.error(res.message);
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Helpers ──
  const groupByCategory = (docs) => {
    const grouped = {};
    (docs || []).forEach((doc) => {
      const key = doc.categoryName;
      if (!grouped[key])
        grouped[key] = {
          categoryName: doc.categoryName,
          categoryDesc: doc.categoryDesc,
          docs: [],
        };
      grouped[key].docs.push(doc);
    });
    return Object.values(grouped);
  };

  const getCategoryIcon = (name) => {
    if (name === "ID Proof") return "🪪";
    if (name === "Address Proof") return "🏠";
    if (name === "Photo") return "📷";
    return "📄";
  };

  const manualGroups = groupByCategory(settingsData?.manualDocs);
  const digilockerGroups = groupByCategory(settingsData?.digilockerDocs);
  const showDigilocker = modeForm.tenantKycMode === "digilocker";
  const showManual = modeForm.tenantKycMode === "manual";
  const showNone = modeForm.tenantKycMode === "not_required";

  if (pageLoading) {
    return (
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">KYC Document Settings</div>
            <div className="page-sub">
              Configure KYC requirements for tenants and co-tenants
            </div>
          </div>
        </div>
        <div className="content">
          <div className="card">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                padding: 8,
              }}
            >
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 14,
                    borderRadius: 6,
                    background:
                      "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.4s infinite",
                    width: i === 0 ? "40%" : "70%",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">KYC Document Settings</div>
            <div className="page-sub">
              Configure KYC requirements for tenants and co-tenants
            </div>
          </div>
        </div>

        <div className="content">
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* KYC Mode Configuration */}
            <div className="card">
              <div className="card-title">KYC Mode Configuration</div>
              <div className="info-box mb14">
                Choose how KYC verification is conducted for tenants and
                co-tenants. This affects what documents are requested during
                onboarding.
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>KYC for Tenant *</label>
                  <select
                    name="tenantKycMode"
                    value={modeForm.tenantKycMode}
                    onChange={handleModeChange}
                  >
                    <option value="digilocker">DigiLocker Auto KYC</option>
                    <option value="manual">Manual KYC (Offline)</option>
                    <option value="not_required">Not Required</option>
                  </select>
                  <div className="input-hint">
                    Controls how the primary tenant is verified
                  </div>
                </div>
                <div className="form-group">
                  <label>KYC for Co-Tenant</label>
                  <select
                    name="coTenantKycMode"
                    value={modeForm.coTenantKycMode}
                    onChange={handleModeChange}
                  >
                    <option value="digilocker">DigiLocker Auto KYC</option>
                    <option value="manual">Manual KYC (Offline)</option>
                    <option value="not_required">Not Required</option>
                  </select>
                  <div className="input-hint">
                    Controls how co-tenants / other members are verified
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveMode}
                  disabled={saveLoading}
                >
                  {saveLoading ? "Saving..." : "Save KYC Mode"}
                </button>
              </div>
            </div>

            {/* DigiLocker Section */}
            {showDigilocker && (
              <div className="card">
                <div className="flex-between mb12">
                  <div>
                    <div className="card-title" style={{ margin: 0 }}>
                      📲 DigiLocker KYC Documents
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-secondary)",
                        marginTop: 3,
                      }}
                    >
                      Documents fetched automatically via DigiLocker API
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openAddModal("digilocker")}
                  >
                    + Add Document
                  </button>
                </div>
                {digilockerGroups.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                      padding: "8px 0",
                    }}
                  >
                    No documents configured.
                  </div>
                ) : (
                  digilockerGroups.map((group) => (
                    <div key={group.categoryName} style={{ marginBottom: 20 }}>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {getCategoryIcon(group.categoryName)}{" "}
                          {group.categoryName}
                        </div>
                        {group.categoryDesc && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                            }}
                          >
                            {group.categoryDesc}
                          </div>
                        )}
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Document Name</th>
                              <th>Remark / Instruction</th>
                              <th>Optional Toggle</th>
                              {/* <th>Status</th> */}
                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.docs.map((doc, index) => (
                              <tr key={doc.docId}>
                                <td className="sn-col">{index + 1}</td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>
                                    {doc.docName}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-secondary)",
                                    maxWidth: 200,
                                  }}
                                >
                                  {doc.description}
                                </td>
                                <td>
                                  <label
                                    className="toggle"
                                    style={{ display: "inline-block" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={doc.isOptional}
                                      onChange={() => handleToggle(doc)}
                                    />
                                    <span className="toggle-slider" />
                                  </label>
                                </td>

                                <td>
                                  <div className="td-actions">
                                    <span
                                      className="action-icon"
                                      onClick={() =>
                                        openEditModal(doc.docId, "digilocker")
                                      }
                                    >
                                      <span className="tip">Edit</span>✎
                                    </span>
                                    <span
                                      className="action-icon red"
                                      onClick={() => {
                                        setDeleteDocId(doc.docId);
                                        setIsModalOpenDelete(true);
                                      }}
                                    >
                                      <span className="tip">Delete</span>🗑
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Manual KYC Section */}
            {showManual && (
              <div className="card">
                <div className="flex-between mb12">
                  <div>
                    <div className="card-title" style={{ margin: 0 }}>
                      📋 Manual KYC Documents
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--text-secondary)",
                        marginTop: 3,
                      }}
                    >
                      Documents uploaded manually by tenant
                    </div>
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => openAddModal("manual")}
                  >
                    + Add Document
                  </button>
                </div>
                {manualGroups.length === 0 ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                      padding: "8px 0",
                    }}
                  >
                    No documents configured.
                  </div>
                ) : (
                  manualGroups.map((group) => (
                    <div key={group.categoryName} style={{ marginBottom: 20 }}>
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {getCategoryIcon(group.categoryName)}{" "}
                          {group.categoryName}
                        </div>
                        {group.categoryDesc && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                            }}
                          >
                            {group.categoryDesc}
                          </div>
                        )}
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table>
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Document Name</th>
                              <th>Description</th>
                              <th>Optional Toggle</th>

                              <th>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.docs.map((doc, index) => (
                              <tr key={doc.docId}>
                                <td className="sn-col">{index + 1}</td>
                                <td>
                                  <div style={{ fontWeight: 600 }}>
                                    {doc.docName}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-secondary)",
                                    maxWidth: 200,
                                  }}
                                >
                                  {doc.description}
                                </td>
                                <td>
                                  <label
                                    className="toggle"
                                    style={{ display: "inline-block" }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={doc.isOptional}
                                      onChange={() => handleToggle(doc)}
                                    />
                                    <span className="toggle-slider" />
                                  </label>
                                </td>

                                <td>
                                  <div className="td-actions">
                                    <span
                                      className="action-icon"
                                      onClick={() =>
                                        openEditModal(doc.docId, "manual")
                                      }
                                    >
                                      <span className="tip">Edit</span>✎
                                    </span>
                                    <span
                                      className="action-icon red"
                                      onClick={() => {
                                        setDeleteDocId(doc.docId);
                                        setIsModalOpenDelete(true);
                                      }}
                                    >
                                      <span className="tip">Delete</span>🗑
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Not Required Section */}
            {showNone && (
              <div className="card">
                <div
                  style={{
                    textAlign: "center",
                    padding: 24,
                    color: "var(--text-secondary)",
                  }}
                >
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🔓</div>
                  <div
                    style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}
                  >
                    KYC Not Required
                  </div>
                  <div style={{ fontSize: 12 }}>
                    Tenants will not be asked to submit any KYC documents. You
                    can change this anytime.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Add Modal ── */}
      {isModalOpenAdd && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 580 }}>
            <div className="modal-header">
              <div className="modal-title">
                Add KYC Document —{" "}
                {addKycMode === "digilocker" ? "DigiLocker" : "Manual"}
              </div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenAdd(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Document Name *</label>

                {/* <select
                  name="docName"
                  value={addForm.docName}
                  onChange={handleAddChange}
                  style={addErrors.docName ? { borderColor: "#dc2626" } : {}}
                >
                  <option value="">Select Document</option>
                  {docTypesLoading ? (
                    <option disabled>Loading...</option>
                  ) : (
                    docTypes.map((d) => (
                      <option key={d.docTypeId} value={d.docTypeCode}>
                        {d.docTypeName}
                      </option>
                    ))
                  )}
                </select> */}
                <select
                  name="docName"
                  value={addForm.docName}
                  onChange={(e) => {
                    const selected = docTypes.find(
                      (d) => d.docTypeCode === e.target.value,
                    );
                    setAddForm((prev) => ({
                      ...prev,
                      docTypeCode: e.target.value,
                      docName: selected ? selected.docTypeName : "",
                    }));
                    setAddErrors((prev) => ({ ...prev, docName: "" }));
                  }}
                  style={addErrors.docName ? { borderColor: "#dc2626" } : {}}
                >
                  <option value="">Select Document</option>
                  {docTypes.map((d) => (
                    <option key={d.docTypeId} value={d.docTypeCode}>
                      {d.docTypeName}
                    </option>
                  ))}
                </select>
                {addErrors.docName && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {addErrors.docName}
                  </span>
                )}
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="categoryId"
                    value={addForm.categoryId}
                    onChange={handleAddChange}
                    style={
                      addErrors.categoryId ? { borderColor: "#dc2626" } : {}
                    }
                  >
                    <option value="">Select Category</option>
                    {categoriesLoading ? (
                      <option disabled>Loading...</option>
                    ) : (
                      categories.map((c) => (
                        <option key={c.categoryId} value={c.categoryId}>
                          {c.categoryName}
                        </option>
                      ))
                    )}
                  </select>
                  {addErrors.categoryId && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {addErrors.categoryId}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={addForm.sortOrder}
                    onChange={handleAddChange}
                    min={0}
                  />
                </div>
              </div>
              <div className="form-group mb12">
                <label>Remark / Instruction for Tenant</label>
                <textarea
                  name="description"
                  value={addForm.description}
                  onChange={handleAddChange}
                  style={{ minHeight: 80 }}
                  placeholder="Instruction shown to tenant when uploading this document…"
                />
              </div>
              <div
                style={{
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--radius-md)",
                  padding: 12,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isOptional"
                      checked={addForm.isOptional}
                      onChange={handleAddChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                  Mark as Optional
                  <span
                    style={{ fontSize: 11, color: "var(--text-secondary)" }}
                  >
                    (if unchecked, document will be Required)
                  </span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenAdd(false)}
                disabled={addLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddSubmit}
                disabled={addLoading}
              >
                {addLoading ? "Saving..." : "Save Document"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal ── */}
      {isModalOpenEdit && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 580 }}>
            <div className="modal-header">
              <div className="modal-title">Edit KYC Document</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenEdit(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Document Name *</label>
                {/* <select
                  name="docName"
                  value={editForm.docName}
                  onChange={handleEditChange}
                  style={editErrors.docName ? { borderColor: "#dc2626" } : {}}
                >
                  <option value="">Select Document</option>
                  {docTypesLoading ? (
                    <option disabled>Loading...</option>
                  ) : (
                    docTypes.map((d) => (
                      <option key={d.docTypeId} value={d.docTypeCode}>
                        {d.docTypeName}
                      </option>
                    ))
                  )}
                    
                </select> */}

                <select
                  name="docName"
                  value={editForm.docTypeCode}
                  onChange={(e) => {
                    const selected = docTypes.find(
                      (d) => d.docTypeCode === e.target.value,
                    );
                    setEditForm((prev) => ({
                      ...prev,
                      docTypeCode: e.target.value,
                      docName: selected ? selected.docTypeName : "",
                    }));
                    setEditErrors((prev) => ({ ...prev, docName: "" }));
                  }}
                  style={editErrors.docName ? { borderColor: "#dc2626" } : {}}
                >
                  <option value="">Select Document</option>
                  {docTypes.map((d) => (
                    <option key={d.docTypeId} value={d.docTypeCode}>
                      {d.docTypeName}
                    </option>
                  ))}
                </select>
                {editErrors.docName && (
                  <span style={{ color: "#dc2626", fontSize: 11 }}>
                    {editErrors.docName}
                  </span>
                )}
              </div>
              <div className="form-grid mb12">
                <div className="form-group">
                  <label>Category *</label>
                  <select
                    name="categoryId"
                    value={editForm.categoryId}
                    onChange={handleEditChange}
                    style={
                      editErrors.categoryId ? { borderColor: "#dc2626" } : {}
                    }
                  >
                    <option value="">Select Category</option>
                    {categoriesLoading ? (
                      <option disabled>Loading...</option>
                    ) : (
                      categories.map((c) => (
                        <option key={c.categoryId} value={c.categoryId}>
                          {c.categoryName}
                        </option>
                      ))
                    )}
                  </select>
                  {editErrors.categoryId && (
                    <span style={{ color: "#dc2626", fontSize: 11 }}>
                      {editErrors.categoryId}
                    </span>
                  )}
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    name="sortOrder"
                    value={editForm.sortOrder}
                    onChange={handleEditChange}
                    min={0}
                  />
                </div>
              </div>
              <div className="form-group mb12">
                <label>Remark / Instruction for Tenant</label>
                <textarea
                  name="description"
                  value={editForm.description}
                  onChange={handleEditChange}
                  style={{ minHeight: 80 }}
                  placeholder="Instruction shown to tenant when uploading this document…"
                />
              </div>
              <div
                style={{
                  background: "var(--bg-secondary)",
                  borderRadius: "var(--radius-md)",
                  padding: 12,
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  <label className="toggle">
                    <input
                      type="checkbox"
                      name="isOptional"
                      checked={editForm.isOptional}
                      onChange={handleEditChange}
                    />
                    <span className="toggle-slider" />
                  </label>
                  Mark as Optional
                  <span
                    style={{ fontSize: 11, color: "var(--text-secondary)" }}
                  >
                    (if unchecked, document will be Required)
                  </span>
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenEdit(false)}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleEditSubmit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {isModalOpenDelete && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 400 }}>
            <div className="modal-header">
              <div className="modal-title">Confirm Delete</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenDelete(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: "var(--text-primary)" }}>
                Are you sure you want to delete this KYC document? This action
                cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenDelete(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteDoc}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Deleting..." : "Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
